import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import debounce from 'lodash/debounce'
import omit from 'lodash/omit'
import classnames from 'classnames'

import Button from '@react/react-spectrum/Button'
import ButtonGroup from '@react/react-spectrum/ButtonGroup'
import FieldLabel from '@react/react-spectrum/FieldLabel'
import Dialog from '@react/react-spectrum/Dialog'
import { Menu, MenuItem } from '@react/react-spectrum/Menu'
import ModalTrigger from '@react/react-spectrum/ModalTrigger'
import NumberInput from '@react/react-spectrum/NumberInput'
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger'
import { Table, THead, TBody, TH, TR, TD } from '@react/react-spectrum/Table'
import Textfield from '@react/react-spectrum/Textfield'
import Tooltip from '@react/react-spectrum/Tooltip'
import { error } from '@react/react-spectrum/Toast/js/ToastContainer'
import Wait from '@react/react-spectrum/Wait'

import BoxExport from '@react/react-spectrum/Icon/BoxExport'
import BoxImport from '@react/react-spectrum/Icon/BoxImport'
import CloudOutline from '@react/react-spectrum/Icon/CloudOutline'
import Delete from '@react/react-spectrum/Icon/Delete'
import Image from '@react/react-spectrum/Icon/Image'

import MediaUpload from 'components/MediaUpload'
import RichTextEditor from 'components/RichTextEditor'

import { useProducts, useProject, useScreens } from 'hooks'

import './ProductsForm.scss'
import { useImsToken } from 'services/ims'
import { useScrapedProductsImport } from './hooks/useScrapedProductsImport'
let demoPilotSdkPromise = null

// cache the SDK promise to avoid loading it multiple times
const getDemoPilotSdk = () => {
  if (!demoPilotSdkPromise) {
    demoPilotSdkPromise = import('@zoltar/demo-pilot-sdk')
  }
  return demoPilotSdkPromise
}

// used for choosing a proper API url inside the Demo Pilot SDK
const ENV = process.env.NODE_ENV === 'production' ? 'prod' : 'stage'

// This debounced function needs to be outside of component.
// Otherwise debounce would get re-initiated with every re-render causing the function to behave like setTimeout instead of debounce.

const renameNewProp = debounce((newName, props, setProduct, setProp) => {
  if (newName && !Object.keys(props).includes(newName)) {
    setProduct((p) => ({ ...p, [newName]: '' }))
    setProp(null)
  }
}, 1000)

const ScreenReference = ({ productId, allowCreate }) => {
  const { openScreen, addProductScreen } = useScreens()
  const { getLinkedScreens } = useProducts()
  const linkedScreens = getLinkedScreens(productId)

  switch (linkedScreens.length) {
    case 0:
      return (
        <Button
          variant='primary'
          quiet
          label='Create Product Screen'
          disabled={!allowCreate}
          onClick={() => addProductScreen(productId)}
        />
      )
    case 1:
      return (
        <Button
          variant='primary'
          quiet
          label='Edit Linked Screen'
          onClick={() => openScreen(linkedScreens[0].meta.name)}
        />
      )
    default:
      return (
        <OverlayTrigger placement='bottom'>
          <Button variant='primary' quiet label='Edit Linked Screens' />
          <Menu placement='bottom'>
            {linkedScreens.map((s) => (
              <MenuItem key={s.meta.name} onClick={() => openScreen(s.meta.name)}>
                {s.meta.title || s.meta.name}
              </MenuItem>
            ))}
          </Menu>
        </OverlayTrigger>
      )
  }
}

const ProductDetails = ({ product, projectId, canEdit, onSave }) => {
  const [localProduct, setLocalProduct] = useState(product)
  const [newProp, setNewProp] = useState(null)
  const { category, description, icon, id, image, video, name, price, ...otherProps } = localProduct || {}

  const onChange = (propName) => (value) => {
    if (canEdit) {
      setLocalProduct((p) => ({ ...p, [propName]: value }))
      onSave({ ...localProduct, [propName]: value })
    }
  }

  const onPropRename = (oldName) =>
    debounce((newName) => {
      setLocalProduct((p) => ({ ...omit(p, oldName), [newName]: p[oldName] }))
    }, 1000)

  const onNewPropRename = (newName) => {
    setNewProp((p) => ({ ...p, name: newName }))
    renameNewProp(newName, otherProps, setLocalProduct, setNewProp)
  }

  const onPropRemove = (propName) => () => {
    if (canEdit) {
      setLocalProduct((p) => omit(p, propName))
      onSave({ ...omit(localProduct, propName) })
    }
  }

  const addProperty = () => canEdit && setNewProp({})

  useEffect(() => {
    if (product?.id !== localProduct?.id) {
      setLocalProduct(product)
    }
  }, [product, localProduct])

  return (
    <>
      <div className='productDetails__header'>
        <div className='productDetails__general'>
          <div style={{ alignItems: 'flex-end' }}>
            <FieldLabel label='Name'>
              <Textfield value={name || ''} onChange={onChange('name')} disabled={!canEdit} />{' '}
            </FieldLabel>
            <ScreenReference productId={localProduct?.id} allowCreate={canEdit && !!name} />
          </div>
          <div>
            <FieldLabel label='Category'>
              <Textfield value={category || ''} onChange={onChange('category')} disabled={!canEdit} />
            </FieldLabel>
            <FieldLabel label='Price'>
              <NumberInput value={price || ''} onChange={onChange('price')} step={'any'} disabled={!canEdit} />
            </FieldLabel>
          </div>

          <div className='productDetails__otherProps'>
            <Table>
              <THead>
                <TH>property</TH>
                <TH>value</TH>
                <TH></TH>
              </THead>
              <TBody>
                {Object.keys(otherProps).map((key) => (
                  <TR key={key}>
                    <TD>
                      <Textfield defaultValue={key || ''} onChange={onPropRename(key)} disabled={!canEdit} />
                    </TD>
                    <TD>
                      <Textfield value={otherProps[key] || ''} onChange={onChange(key)} disabled={!canEdit} />
                    </TD>
                    <TD>
                      <Button variant='tool' icon={<Delete />} onClick={onPropRemove(key)} disabled={!canEdit} />
                    </TD>
                  </TR>
                ))}
                {newProp && (
                  <TR>
                    <TD>
                      <Textfield
                        value={newProp.name || ''}
                        onChange={onNewPropRename}
                        invalid={Object.keys(otherProps).includes(newProp.name)}
                        disabled={!canEdit}
                      />
                    </TD>
                    <TD>
                      <Textfield disabled />
                    </TD>
                    <TD>
                      <Button variant='tool' icon={<Delete />} onClick={() => setNewProp(null)} disabled={!canEdit} />
                    </TD>
                  </TR>
                )}
                <TR onClick={addProperty}>
                  <TD>
                    <Button variant='action' quiet label='+ Add' disabled={!canEdit || !!newProp} />
                  </TD>
                  <TD />
                  <TD />
                </TR>
              </TBody>
            </Table>
          </div>
        </div>
        <div className='productDetails__image'>
          <FieldLabel label='Image'>
            <MediaUpload
              type='image'
              value={image}
              heading='Product Image'
              projectId={projectId}
              onUpload={onChange('image')}
              withTextfield={true}
              disabled={!canEdit}
            />
          </FieldLabel>
          <FieldLabel label='Video'>
            <MediaUpload
              type='video'
              value={video || ''}
              heading='Product Video'
              projectId={projectId}
              onUpload={onChange('video')}
              withTextfield={true}
              disabled={!canEdit}
            />
          </FieldLabel>
        </div>
      </div>
      <FieldLabel label='Description'>
        <RichTextEditor
          key={product?.id}
          controlledContent={description || ''}
          onChange={onChange('description')}
          debounce={false}
          height={300}
          disabled={!canEdit}
        />
      </FieldLabel>
    </>
  )
}

const ProductsForm = () => {
  const fileInputRef = useRef()
  const { itemId, projectId } = useParams()
  const { products, addProduct, getLinkedScreens, saveProduct, removeProduct, updateProducts, canEdit } = useProducts()
  const { project } = useProject()
  const livedemosWorkspaceId = project?.workspace?.livedemosWorkspaceId ?? null
  const [selectedId, setSelectedId] = useState(itemId)
  const [sortDescriptor, setSortDescriptor] = useState({ key: 'name', direction: 1 })
  const selectedProduct = useMemo(() => products?.find((p) => p.id === selectedId), [products, selectedId])
  const { data: imsTokenData, loading: imsTokenLoading, error: imsTokenError } = useImsToken()
  const [isImportProductsDialogLoading, setIsImportProductsDialogLoading] = useState(false)

  const { importScrapedProducts } = useScrapedProductsImport({ projectId, livedemosWorkspaceId })

  const toggleSort = (key) => {
    setSortDescriptor((sd) => ({ key, direction: sd.key === key ? -sd.direction : 1 }))
  }

  // remove null values should they appear and sort products based on key and direction
  const sortedProducts = products
    .filter(Boolean)
    .sort((a, b) => sortDescriptor.direction * a[sortDescriptor.key].localeCompare(b[sortDescriptor.key]))

  const getDeleteMessage = (id) => {
    if (getLinkedScreens(id).length) {
      return (
        <>
          <p>
            This product is currently referenced by{' '}
            {getLinkedScreens(id)
              .map((s) => s.title || s.name)
              .join(', ')}
            .
          </p>
          <p>Do you want to delete it anyway?</p>
        </>
      )
    }
    return <p>This product is not referenced on any screen. Do you want to delete it?</p>
  }

  const exportToFile = () => {
    const url = window.URL.createObjectURL(
      new Blob([JSON.stringify(products)], {
        type: 'application/json',
      })
    )
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `${projectId}-products.json`)
    document.body.appendChild(link)
    link.click()
  }

  const importFromFile = async (ev) => {
    try {
      const file = ev.target.files[0]
      const text = await file.text()
      const _products = JSON.parse(text)
      updateProducts(_products)
    } catch (err) {
      error('Could not import products. ' + err)
    }
  }

  if (imsTokenError) {
    console.error('Error getting IMS token for customer domain import:', imsTokenError)
  }

  const loading = imsTokenLoading || isImportProductsDialogLoading

  return (
    <div className='products'>
      <section className='products__list'>
        <ButtonGroup value='' disabled={!canEdit}>
          <Button label='Export to file' value='export' icon={<BoxExport />} onClick={exportToFile} />
          <Button
            label='Import from file'
            value='importFile'
            icon={<BoxImport />}
            onClick={() => fileInputRef?.current?.click()}
          />
          {!imsTokenError && (
            <Button
              value='importZoltarDemoPilot'
              icon={
                <img
                  src='https://stage.pilot.adobedemo.com/assets/logo-vA01btbO.svg'
                  alt='Zoltar Demo Pilot logo'
                  width='18px'
                  style={{ marginRight: '8px' }}
                />
              }
              onClick={async () => {
                setIsImportProductsDialogLoading(true)
                // import the dialog opener dynamically to avoid bundling the whole SDK with the main bundle
                const { ImportProductsDialogOpener } = await getDemoPilotSdk()
                ImportProductsDialogOpener.open({
                  env: ENV,
                  accessToken: imsTokenData.imsToken,
                  colorScheme: 'light',
                  onResultProcessing: async (result, reportMessage) => {
                    try {
                      const transformed = await importScrapedProducts(result.products, reportMessage)
                      updateProducts(transformed)
                    } catch (err) {
                      console.error('Failed to process scraped products:', err)
                      error('Could not import scraped products. ' + (err?.message || ''))
                      throw err
                    }
                  },
                  onError: (err) => {
                    console.error('Error:', err)
                    setIsImportProductsDialogLoading(false)
                  },
                })
                setIsImportProductsDialogLoading(false)
              }}
              disabled={loading}
            >
              {loading ? <Wait size='S' /> : 'Scrape from URL'}
            </Button>
          )}
        </ButtonGroup>
        <input ref={fileInputRef} type='file' style={{ display: 'none' }} onChange={importFromFile} />
        <Table>
          <THead>
            <TH width={75}></TH>
            <TH onClick={() => toggleSort('name')}>Name</TH>
            <TH width={50}>ID</TH>
            <TH onClick={() => toggleSort('category')}>Category</TH>
            <TH width={105}></TH>
          </THead>
          <TBody>
            <TR onClick={addProduct}>
              <TD>
                <Button variant='action' quiet label='+ Add' disabled={!canEdit} />
              </TD>
              <TD />
              <TD />
              <TD />
              <TD />
            </TR>
            {sortedProducts.map((product) => (
              <TR
                key={product.id}
                onClick={() => setSelectedId(product.id)}
                className={classnames({ 'is-selected': product.id === selectedId })}
              >
                <TD>
                  <div className='products__thumbnail'>
                    {product.thumbnail || product.image ? (
                      <img src={product.thumbnail || product.image} alt='product' />
                    ) : (
                      <Image size='XL' />
                    )}
                  </div>
                </TD>
                <TD>{product.name}</TD>
                <TD>{product.id}</TD>
                <TD>{product.category}</TD>
                <TD style={{ textAlign: 'right' }}>
                  {product?.importedFrom && (
                    <OverlayTrigger>
                      <Button variant='tool' icon={<CloudOutline />} />
                      <Tooltip>Imported from {product.importedFrom}</Tooltip>
                    </OverlayTrigger>
                  )}
                  <ModalTrigger>
                    <Button
                      variant='tool'
                      icon={<Delete />}
                      onClick={(e) => {
                        e.stopPropagation()
                      }}
                      disabled={!canEdit}
                    />
                    <Dialog
                      variant='destructive'
                      title='Warning'
                      confirmLabel='Remove'
                      cancelLabel='Cancel'
                      onConfirm={() => {
                        removeProduct(product.id)
                        setSelectedId(null)
                      }}
                    >
                      {getDeleteMessage(product.id)}
                    </Dialog>
                  </ModalTrigger>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </section>
      {selectedProduct && (
        <section className='products__details'>
          <ProductDetails
            key={selectedProduct?.id}
            product={selectedProduct}
            projectId={projectId}
            onSave={saveProduct}
            canEdit={canEdit}
          />
        </section>
      )}
    </div>
  )
}

export default ProductsForm
